/**
 * useShellStory - the story's tools on this screen: the cast, clocks, the
 * board, the GM's notes, and which panel the right column shows.
 *
 * `story.canDirect` is who may write them: the GM, or anyone outside a
 * session (where the lists are local).
 *
 * A clock that becomes full says so in the log and shows the board entry it
 * was to reveal, once (`onFull`); a per-round clock fills as the combat
 * round moves, on the directing screen (`useClockRounds`).
 *
 * @param {object} s - the shell scope: sessionId, user, isGm, direction,
 *   roster, setRoster, updateCombatantSheet, setActiveTab, turnTracker,
 *   addRollNarrative.
 * @returns {{ story: { clocks, board, cast, notes, timer, canDirect: boolean, passTime: Function }, rightFront }}
 */
import { useCallback, useMemo, useRef } from 'react';
import { clockRiders, fullLine } from '../engine/narrative/clocks';
import { castOf, personInOneLine, withPersona } from '../engine/narrative/cast';
import * as multiplayer from '../multiplayer';
import { useBoard, useClocks } from './useStoryBoards';
import useRightFront from './useRightFront';
import useGmNotes from './useGmNotes';
import { startTimer } from '../engine/narrative/timer';
import useClockRounds from './useClockRounds';

/**
 * The decision timer (`engine/narrative/timer`): the one on the frame, and
 * the director's two verbs. Starting one writes a length and this moment;
 * every screen counts down from that by its own clock.
 */
function useTimer(direction) {
  const live = useRef(direction);
  live.current = direction;
  const value = direction.frame?.timer ?? null;
  const start = useCallback((seconds) => live.current.direct({ timer: startTimer(seconds, Date.now()) }), []);
  const clear = useCallback(() => live.current.direct({ timer: null }), []);
  return useMemo(() => ({ value, start, clear }), [value, start, clear]);
}

/** The log's line for it: "Time passes." or "Time passes: 3 beats." */
const timeLine = (beats) => (beats === 1 ? 'Time passes.' : `Time passes: ${beats} beats.`);

/**
 * TIME PASSES, from this screen: every combatant's beats (the tracker's
 * `passTime`, one undoable step), the frame's beat count moved on so every
 * screen's recharging weapons tick, and a line in the log. The snapshot is
 * taken inside `passTime`, before the count moves, so an undo puts both back.
 * One beat unless told otherwise (a clock that marks time filled several
 * segments at once).
 */
function usePassTime(s) {
  const live = useRef(s);
  live.current = s;
  return useCallback((beats = 1) => {
    const now = live.current;
    // The button hands over its click event: anything that is not a count of beats is one beat.
    const count = Number.isInteger(beats) && beats > 0 ? beats : 1;
    now.turnTracker.passTime(count);
    now.direction.direct({ beats: (now.direction.frame?.beats ?? 0) + count });
    now.addRollNarrative({ type: 'info', lane: 'story', text: timeLine(count), timestamp: Date.now() });
  }, []);
}

/**
 * The roster as people (`engine/narrative/cast`), with the director's two
 * edits - both ordinary roster writes - and `open`, which puts a person in
 * the target panel and that panel in front.
 */
function useCast(s, showFront) {
  const { roster, setRoster, updateCombatantSheet, setActiveTab } = s;
  const partyTeam = s.direction.frame?.partyTeam ?? null;
  const people = useMemo(() => castOf(roster, partyTeam), [roster, partyTeam]);
  const setPersona = useCallback(
    (id, patch) => updateCombatantSheet(id, (sheet) => withPersona(sheet, patch)),
    [updateCombatantSheet],
  );
  const addPerson = useCallback(
    (line) => {
      const person = personInOneLine(line);
      if (person) setRoster((was) => [...was, person]);
    },
    [setRoster],
  );
  const open = useCallback(
    (id) => {
      setActiveTab(id);
      showFront('target');
    },
    [setActiveTab, showFront],
  );
  return useMemo(() => ({ ...people, setPersona, addPerson, open }), [people, setPersona, addPerson, open]);
}

/**
 * What a clock does the moment it becomes full: its line in the log, and the
 * board entry it was to show. The board comes through a ref - it is made
 * after the clocks.
 */
function useOnFull(s, boardRef) {
  const live = useRef(s);
  live.current = s;
  return useCallback(
    (clock) => {
      live.current.addRollNarrative({ type: 'info', lane: 'story', text: fullLine(clock), timestamp: Date.now() });
      const { reveal } = clockRiders(clock);
      if (reveal) boardRef.current?.edit(reveal, { visible: true });
    },
    [boardRef],
  );
}

export default function useShellStory(s) {
  const uid = s.user?.uid ?? null;
  const seat = useMemo(
    () => ({ sessionId: s.sessionId, uid, isGm: !!s.isGm, mp: multiplayer }),
    [s.sessionId, uid, s.isGm],
  );
  const passTime = usePassTime(s);
  const boardRef = useRef(null);
  const onFull = useOnFull(s, boardRef);
  // A clock that marks time: each segment filled on it is a beat, however many are filled at once.
  const clocks = useClocks(seat, passTime, onFull);
  const board = useBoard(seat);
  boardRef.current = board;
  const canDirect = !s.sessionId || !!s.isGm;
  useClockRounds(canDirect, s.turnTracker?.trackerState, clocks.clocks, clocks.tickRound);
  const rightFront = useRightFront(s.direction.shown);
  const cast = useCast(s, rightFront.show);
  const notes = useGmNotes({ sessionId: s.sessionId, isGm: !!s.isGm, mp: multiplayer });
  const timer = useTimer(s.direction);
  const story = useMemo(
    () => ({ clocks, board, cast, notes, timer, canDirect, passTime }),
    [clocks, board, cast, notes, timer, canDirect, passTime],
  );
  return { story, rightFront };
}
